import React, { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Paper, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { Course } from '../../lib/interfaces/Course';
import LinearProgressWithLabel from '../Simple/LinearProgress/LinearProgressWithLabel';
import { findFirstIncompletedTask } from '../../lib/helpers/courses.helper';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import { IEntityContentField } from '../../lib/interfaces/Common';
import { FIELD_TYPES } from '../Form/form.utils';
import ContentRenderer from '../Smart/Content/ContentRenderer';
import './CoursesListItem.scss';

type CoursesListItemProps = {
	courseData: Course;
};

const CoursesListItem: FC<CoursesListItemProps> = ({ courseData }) => {
	const { t, i18n } = useTranslation();
	const { data: currentUser } = useUserMeQuery();
	const navigate = useNavigate();
	const { imageUrl, tags, slug } = courseData;
	const currentLanguage: string = i18n.language;
	const contentFields: IEntityContentField[] =
		courseData.content[currentLanguage];

	const firstIncompletedTask = useMemo(
		() => (currentUser ? findFirstIncompletedTask(courseData, currentUser) : 0),
		[currentUser?.completedTasks, courseData.tasks],
	);
	const handleGoToCourse = () => {
		navigate(`/courses/${slug}/${firstIncompletedTask}`);
	};

	const classes = courseData.customClass
		? ['CoursesListItem', ...courseData.customClass]
		: ['CoursesListItem'];

	const courseClasses = classes.join(' ');

	return (
		<Paper className={courseClasses} elevation={0} variant="outlined">
			<div className="CoursesListItem__top">
				<div className="CoursesListItem__image-container">
					<div className="CoursesListItem__free-label">Free</div>
					<img
						src={imageUrl}
						alt={courseData.name}
						className="CoursesListItem__image-container__image"
					/>
				</div>
				<div className="CoursesListItem__content">
					{contentFields.map((field) => {
						if (field.type === FIELD_TYPES.TEXT) {
							return (
								<Typography variant="h5" gutterBottom component="div" key={field.name}>
									{field.content}
								</Typography>
							);
						}
						if (field.type === FIELD_TYPES.RICH_EDITOR) {
							return <ContentRenderer html={field.content} key={field.name} />;
						}
						return null;
					})}
					{tags?.map((tag: string, idx: number) => (
						<Chip
							key={idx} // eslint-disable-line react/no-array-index-key
							label={tag}
							size="small"
							variant="outlined"
							className="CoursesListItem__tag"
							sx={{ marginBottom: '5px' }}
						/>
					))}
				</div>
			</div>
			<div className="CoursesListItem__bottom">
				<div className="CoursesListItem__to-course">
					<Button variant="contained" disableElevation onClick={handleGoToCourse}>
						{t('goToCourse')}
					</Button>
					<Paper className="CoursesListItem__to-course__progress" variant="outlined">
						<LinearProgressWithLabel
							variant="determinate"
							value={courseData.progress || 0}
						/>
					</Paper>
				</div>
			</div>
		</Paper>
	);
};

export default CoursesListItem;
